import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ApiError,
  api,
  type ContentSessionRow,
  type LicenceRow,
  type QueueEntryRow,
  type UtilisationRow,
} from '../lib/api'
import { Banner, Empty, ErrorNote, RecordGrid } from '../components/Page'
import { Card, ConsoleHead } from '../components/admin/Console'

/** Whole days between two dates, floored. */
function daysBetween(from: string, to: string): number {
  const a = new Date(`${from}T00:00:00`).getTime()
  const b = new Date(`${to}T00:00:00`).getTime()
  if (Number.isNaN(a) || Number.isNaN(b)) return 0
  return Math.round((b - a) / 86_400_000)
}

function minutesSince(iso: string): number {
  return Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000))
}

/**
 * Live seats, the waiting queue, and the trial a licence may be running under.
 *
 * A trial is a licence with `is_trial` set: the same enforcement, the same
 * reporting, and no separate code path. That is why this screen shows one and
 * not a different screen for it.
 */
export function SeatsPage() {
  const queryClient = useQueryClient()
  const [licenceId, setLicenceId] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)

  const licences = useQuery({
    queryKey: ['admin', 'licences'],
    queryFn: () => api.get<LicenceRow[]>('/admin/licences'),
  })

  const licence = licences.data?.find((row) => row.id === licenceId) ?? licences.data?.[0]
  const trial = licences.data?.find((row) => row.is_trial)

  const utilisation = useQuery({
    queryKey: ['admin', 'utilisation', licence?.id],
    queryFn: () => api.get<UtilisationRow>(`/admin/licences/${licence!.id}/utilisation`),
    enabled: !!licence,
    refetchInterval: 10_000,
  })

  const sessions = useQuery({
    queryKey: ['admin', 'sessions', licence?.id],
    queryFn: () =>
      api.get<ContentSessionRow[]>(`/admin/content-sessions?licence_id=${licence!.id}`),
    enabled: !!licence,
    refetchInterval: 10_000,
  })

  const queue = useQuery({
    queryKey: ['admin', 'queue', licence?.id],
    queryFn: () => api.get<QueueEntryRow[]>(`/admin/licences/${licence!.id}/queue`),
    enabled: !!licence,
    refetchInterval: 10_000,
  })

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'sessions'] })
    void queryClient.invalidateQueries({ queryKey: ['admin', 'queue'] })
    void queryClient.invalidateQueries({ queryKey: ['admin', 'utilisation'] })
  }

  const evict = useMutation({
    mutationFn: (id: string) => api.del<ContentSessionRow>(`/admin/content-sessions/${id}`),
    onSuccess: refresh,
  })

  const sweep = useMutation({
    mutationFn: () => api.post<UtilisationRow>(`/admin/licences/${licence!.id}/sweep`),
    onSuccess: refresh,
  })

  /**
   * Offering a freed seat to a named reader.
   *
   * The queue is ordered by arrival and that order is the fairness guarantee,
   * so the API says when an offer jumped somebody and refuses an offer to a
   * reader who is not waiting. Both answers are shown as written rather than
   * flattened into "something went wrong".
   */
  const offer = useMutation({
    mutationFn: (input: { licence_id: string; user_id: string }) =>
      api.post<{ message: string; jumped: number; claim_window_s: number | null }>(
        '/admin/seats/offer',
        input,
      ),
    onSuccess: (result) => {
      setNote(result.message)
      refresh()
    },
    onError: (error) => setNote(error instanceof ApiError ? error.detail : String(error)),
  })

  const extend = useMutation({
    mutationFn: (input: { id: string; body: Record<string, unknown> }) =>
      api.patch<LicenceRow>(`/admin/licences/${input.id}`, input.body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'licences'] }),
  })

  const live = sessions.data ?? []
  const waiting = queue.data ?? []

  return (
    <>
      <ConsoleHead
        title="Trial management"
        actions={
          (licences.data?.length ?? 0) > 1 ? (
            <select
              className="input-dense w-auto"
              aria-label="Licence"
              value={licence?.id ?? ''}
              onChange={(event) => setLicenceId(event.target.value)}
            >
              {licences.data?.map((row) => (
                <option key={row.id} value={row.id}>
                  {row.institution_name} × {row.publication_name}
                </option>
              ))}
            </select>
          ) : undefined
        }
      />

      <ErrorNote error={evict.error ?? sweep.error ?? extend.error} />

      {note && (
        <div className="mb-4">
          <Banner tone="neutral">{note}</Banner>
        </div>
      )}

      <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div>
          {trial ? (
            <TrialCard
              licence={trial}
              utilisation={trial.id === licence?.id ? utilisation.data : undefined}
              pending={extend.isPending}
              onExtend={() => {
                const base = trial.ends_on ? new Date(`${trial.ends_on}T00:00:00`) : new Date()
                base.setDate(base.getDate() + 14)
                extend.mutate({
                  id: trial.id,
                  body: { ends_on: base.toISOString().slice(0, 10) },
                })
              }}
              onConvert={() => extend.mutate({ id: trial.id, body: { is_trial: false } })}
            />
          ) : (
            <Card>
              <Empty>
                No trial is running. A trial is a licence with{' '}
                <span className="font-mono text-[13px]">is_trial</span> set, so it is created the
                same way a licence is and appears here with its day count, its scope and what it has
                been used for.
              </Empty>
            </Card>
          )}
        </div>

        <div>
          <ConsoleHead
            title="Live sessions and queue"
            note={
              utilisation.data
                ? `${utilisation.data.seats_in_use} of ${utilisation.data.seat_limit ?? 'unlimited'} in use · ${utilisation.data.queue_depth} waiting · peak ${utilisation.data.peak_today} today`
                : undefined
            }
            actions={
              licence && (
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={sweep.isPending}
                  onClick={() => sweep.mutate()}
                  title="Ends sessions that have passed their idle or hard limit and offers the freed places to the head of the queue."
                >
                  Reap expired now
                </button>
              )
            }
          />
          <Card>
            {!licence ? (
              <Empty>No licence exists, so there is nothing to hold a session against.</Empty>
            ) : !live.length && !waiting.length ? (
              <Empty>
                Nobody is reading and nobody is waiting. A row appears the moment a reader
                opens a page under this licence, and the place is released when they leave or when
                the idle timeout reaps it.
              </Empty>
            ) : (
              <>
                {live.map((session, index) => (
                  <div
                    key={session.id}
                    className="flex items-center gap-[11px] px-4 py-3"
                    style={{
                      borderBottom:
                        index < live.length + waiting.length - 1
                          ? '1px solid var(--color-neutral-300)'
                          : 'none',
                    }}
                  >
                    <span className="h-2 w-2 flex-none rounded-full bg-art" aria-hidden="true" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[14.5px] font-semibold">
                        {session.user_email ?? 'unnamed account'}
                      </div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.04em] text-ink2">
                        Reading · idle {session.idle_seconds ?? 0} s ·{' '}
                        {session.ip ?? 'no address'}
                      </div>
                    </div>
                    <div className="flex-none font-mono text-[11px] text-ink2">
                      {minutesSince(session.started_at)} min
                    </div>
                    <button
                      type="button"
                      className="flex-none text-[13px] text-mark"
                      disabled={evict.isPending}
                      onClick={() => evict.mutate(session.id)}
                    >
                      End session
                    </button>
                  </div>
                ))}
                {waiting.map((entry, index) => (
                  <div
                    key={entry.id}
                    className="flex items-center gap-[11px] px-4 py-3"
                    style={{
                      borderBottom:
                        index < waiting.length - 1 ? '1px solid var(--color-neutral-300)' : 'none',
                    }}
                  >
                    <span className="h-2 w-2 flex-none rounded-full bg-rule2" aria-hidden="true" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[14.5px] font-semibold">
                        {entry.user_email ?? 'unnamed account'}
                      </div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.04em] text-ink2">
                        Queued · position {entry.position ?? '?'} · {entry.status}
                      </div>
                    </div>
                    <div className="flex-none font-mono text-[11px] text-ink2">
                      {minutesSince(entry.requested_at)} min wait
                    </div>
                    <button
                      type="button"
                      className="flex-none text-[13px] text-accent"
                      disabled={offer.isPending || !licence}
                      title="Hands the freed place to this reader, with a claim window. Their place in the queue is what the offer is made against."
                      onClick={() =>
                        offer.mutate({ licence_id: licence!.id, user_id: entry.user_id })
                      }
                    >
                      Offer place
                    </button>
                  </div>
                ))}
              </>
            )}
          </Card>
        </div>
      </div>
    </>
  )
}

function TrialCard({
  licence,
  utilisation,
  pending,
  onExtend,
  onConvert,
}: {
  licence: LicenceRow
  utilisation: UtilisationRow | undefined
  pending: boolean
  onExtend: () => void
  onConvert: () => void
}) {
  const today = new Date().toISOString().slice(0, 10)
  const span = licence.starts_on && licence.ends_on ? daysBetween(licence.starts_on, licence.ends_on) : 0
  const elapsed = licence.starts_on ? daysBetween(licence.starts_on, today) : 0
  const share = span > 0 ? Math.min(1, Math.max(0, elapsed / span)) : 0

  return (
    <Card className="px-[18px] py-4">
      <div className="mb-3 flex flex-wrap items-baseline gap-2.5">
        <h2 className="text-[20px] font-semibold leading-[1.15]">
          Trial · {span || '?'} days
        </h2>
        <span
          className="rounded-[--radius-md] border px-1.5 py-[2px] font-mono text-[10px] uppercase"
          style={{
            background: 'var(--jl-accent-100)',
            borderColor: 'var(--jl-accent-200)',
            color: 'var(--jl-accent)',
          }}
        >
          Day {elapsed}
        </span>
      </div>

      <div className="mb-[7px] h-2 overflow-hidden rounded-[1px] bg-n300">
        <div className="h-full bg-accent" style={{ width: `${share * 100}%` }} />
      </div>
      <div className="mb-3.5 flex justify-between font-mono text-[10px] uppercase text-ink2">
        <div>Started {licence.starts_on ?? 'not set'}</div>
        <div>Ends {licence.ends_on ?? 'not set'}</div>
      </div>

      <div className="mb-3.5">
        <RecordGrid
          cols={1}
          rows={[
            ['institution', licence.institution_name ?? 'unknown'],
            [
              'scope',
              `${licence.publication_name ?? 'unknown'} · ${licence.starts_on ?? '?'} to ${licence.ends_on ?? '?'}`,
            ],
            [
              'users',
              licence.access_model === 'unlimited'
                ? 'unlimited'
                : `${licence.concurrent_limit ?? licence.named_seat_limit ?? 0} ${licence.access_model}`,
            ],
            [
              'rights',
              [
                'read',
                licence.permits_download ? 'download' : null,
                licence.permits_export ? 'export' : null,
              ]
                .filter(Boolean)
                .join(' · '),
            ],
            ['sessions today', String(utilisation?.sessions_today ?? 0)],
            ['reading now', String(utilisation?.seats_in_use ?? 0)],
            ['peak all time', String(utilisation?.peak_all_time ?? 0)],
          ]}
        />
      </div>

      <div className="flex flex-wrap gap-[7px]">
        <button
          type="button"
          className="btn-primary"
          disabled={pending}
          title="Clears is_trial. Nothing else changes: the enforcement and the reporting were already those of a licence."
          onClick={onConvert}
        >
          {pending ? 'Working…' : 'Convert to a licence'}
        </button>
        <button type="button" className="btn-secondary" disabled={pending} onClick={onExtend}>
          {pending ? 'Extending…' : 'Extend 14 days'}
        </button>
      </div>

      <p className="mt-3 font-mono text-[9.5px] leading-[1.7] text-ink2">
        A TRIAL IS A LICENCE WITH IS_TRIAL SET · SAME ENFORCEMENT,
        <br />
        SAME REPORTING, NO SEPARATE CODE PATH.
      </p>
    </Card>
  )
}
